// Bins an element's field paths into Fill / Stroke / Typography / Layout eyebrows
// and detects the element-row visual variant. Pure module — no React, no I/O.
//
// Inputs:
//   paths: string[]                    — registry paths bound to the element
//   getPropType: (path) => string|null — returns the property type (e.g. 'color', 'font', 'fontSize'…)
//
// Output:
//   binFieldsIntoEyebrows -> Array<{ id, title, fields: string[] }>
//   detectElementVariant -> 'color-only' | 'toggle-only' | 'badge' | 'text' | 'plain'

const FILL_SURFACE_PAINT_KEYS = [
  'backgroundColor',
  'selectedBackgroundColor',
  'focusBackgroundColor',
  'hoverBackgroundColor',
];

const FILL_TEXT_PAINT_KEYS = [
  'color',
  'textColor',
  'focusColor',
  'focusTextColor',
  'hoverColor',
  'hoverTextColor',
  'placeholderColor',
];

const STROKE_PAINT_KEYS = [
  'borderColor',
  'selectedBorderColor',
  'focusBorderColor',
  'hoverBorderColor',
];

const STROKE_GEOMETRY_KEYS = ['borderStyle', 'borderWidth', 'borderRadius', 'shape'];

const TYPOGRAPHY_TYPES = new Set(['font', 'fontStyle', 'fontWeight', 'fontSize']);

const LAYOUT_KEY_TAILS = new Set([
  'gap',
  'padding',
  'paddingX',
  'paddingY',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'shadowWidth',
  'backgroundImage',
  'backgroundPosition',
  'backgroundWidth',
  'markSize',
  'size',
  'alignment',
]);

const FILL_ORDER = [...FILL_SURFACE_PAINT_KEYS, ...FILL_TEXT_PAINT_KEYS];
const STROKE_ORDER = [...STROKE_PAINT_KEYS, ...STROKE_GEOMETRY_KEYS];
const TYPOGRAPHY_TYPE_ORDER = ['font', 'fontStyle', 'fontWeight', 'fontSize'];

export const EYEBROW_IDS = {
  QUANTITY: 'quantity',
  ICON: 'icon',
  FILL: 'fill',
  MARKER: 'marker',
  STROKE: 'stroke',
  TYPOGRAPHY: 'typography',
  LAYOUT: 'layout',
};

const EYEBROW_TITLES = {
  quantity: 'Quantity',
  icon: 'Icons',
  fill: 'Fill',
  marker: 'Marker',
  stroke: 'Stroke',
  typography: 'Typography',
  layout: 'Layout',
};

// Quantity and Icons are opt-in through `overrides`, never classified into.
const EYEBROW_RENDER_ORDER = [
  'quantity',
  'icon',
  'fill',
  'marker',
  'stroke',
  'typography',
  'layout',
];

function lastSegment(path) {
  const i = path.lastIndexOf('.');
  return i === -1 ? path : path.slice(i + 1);
}

// Style state prefixes that wrap the actual property segment.
// e.g. `…input.focusStyles.color` → semantic key `focusColor`.
const STATE_PREFIXES = ['focus', 'selected', 'hover', 'placeholder'];

// Compute a prefix-aware semantic key so the same property under different
// style states (styles / focusStyles / placeholderStyles) sorts independently
// and matches keys defined in FILL_*/STROKE_* ORDER constants.
function semanticKey(path) {
  const segments = path.split('.');
  if (segments.length < 2) return lastSegment(path);
  const stylesSeg = segments[segments.length - 2];
  const lastSeg = segments[segments.length - 1];
  if (stylesSeg === 'styles') return lastSeg;
  if (stylesSeg.endsWith('Styles')) {
    const prefix = stylesSeg.slice(0, -'Styles'.length);
    if (STATE_PREFIXES.includes(prefix)) {
      return prefix + lastSeg.charAt(0).toUpperCase() + lastSeg.slice(1);
    }
  }
  return lastSeg;
}

function classify(path, propType) {
  const tail = lastSegment(path);
  const sem = semanticKey(path);

  if (TYPOGRAPHY_TYPES.has(propType)) return EYEBROW_IDS.TYPOGRAPHY;

  if (
    STROKE_PAINT_KEYS.includes(tail) ||
    STROKE_PAINT_KEYS.includes(sem) ||
    STROKE_GEOMETRY_KEYS.includes(tail) ||
    STROKE_GEOMETRY_KEYS.includes(sem)
  ) {
    return EYEBROW_IDS.STROKE;
  }

  if (propType === 'color') return EYEBROW_IDS.FILL;

  if (LAYOUT_KEY_TAILS.has(tail)) return EYEBROW_IDS.LAYOUT;

  return EYEBROW_IDS.LAYOUT;
}

function compareByOrder(orderList, key) {
  const i = orderList.indexOf(key);
  return i === -1 ? Number.POSITIVE_INFINITY : i;
}

function orderFields(binId, paths, getPropType) {
  if (binId === EYEBROW_IDS.FILL) {
    return [...paths].sort(
      (a, b) =>
        compareByOrder(FILL_ORDER, semanticKey(a)) - compareByOrder(FILL_ORDER, semanticKey(b)),
    );
  }
  if (binId === EYEBROW_IDS.STROKE) {
    return [...paths].sort(
      (a, b) =>
        compareByOrder(STROKE_ORDER, semanticKey(a)) - compareByOrder(STROKE_ORDER, semanticKey(b)),
    );
  }
  if (binId === EYEBROW_IDS.TYPOGRAPHY) {
    return [...paths].sort(
      (a, b) =>
        compareByOrder(TYPOGRAPHY_TYPE_ORDER, getPropType(a)) -
        compareByOrder(TYPOGRAPHY_TYPE_ORDER, getPropType(b)),
    );
  }
  return paths;
}

// `overrides` (optional) maps a path to an eyebrow id, letting an element
// re-home specific fields — e.g. the price-picker Check mark sends `shape` +
// `color` to the Marker eyebrow instead of Stroke/Fill. Overridden bins keep
// the element's declared field order (no ORDER-list sorting).
export function binFieldsIntoEyebrows(paths, getPropType, overrides = {}) {
  const bins = Object.fromEntries(EYEBROW_RENDER_ORDER.map(id => [id, []]));
  for (const p of paths) {
    const binId = overrides[p] && bins[overrides[p]] ? overrides[p] : classify(p, getPropType(p));
    bins[binId].push(p);
  }
  return EYEBROW_RENDER_ORDER.map(id => ({
    id,
    title: EYEBROW_TITLES[id],
    fields: orderFields(id, bins[id], getPropType),
  })).filter(eb => eb.fields.length > 0);
}

export function detectElementVariant(paths, getPropType) {
  if (paths.length === 0) return 'plain';

  const types = paths.map(getPropType);

  if (paths.length === 1 && types[0] === 'toggle') return 'toggle-only';
  if (paths.length === 1 && types[0] === 'color') return 'color-only';

  // Text: 1 primary color + only typography (and optionally layout) fields
  const colorPaths = paths.filter((_, i) => types[i] === 'color');
  const typoPaths = paths.filter((_, i) => TYPOGRAPHY_TYPES.has(types[i]));
  const otherPaths = paths.filter(
    (_, i) => types[i] !== 'color' && !TYPOGRAPHY_TYPES.has(types[i]),
  );
  const otherIsLayoutOnly = otherPaths.every(
    p => classify(p, getPropType(p)) === EYEBROW_IDS.LAYOUT,
  );
  if (colorPaths.length === 1 && typoPaths.length > 0 && otherIsLayoutOnly) return 'text';

  // Badge variant is opt-in via `variant: 'badge'` in the registry — never auto-detected.
  return 'plain';
}

export function isTextPaintTail(tail) {
  return FILL_TEXT_PAINT_KEYS.includes(tail);
}

// Helpers exported for tests
export const __testing = {
  classify,
  lastSegment,
  FILL_ORDER,
  STROKE_ORDER,
  TYPOGRAPHY_TYPE_ORDER,
  FILL_TEXT_PAINT_KEYS,
};
